/* eslint-disable no-unreachable-loop */
import { FC, useEffect, useState } from 'react';
import Header from '@/components/Header/Header';
import styles from './NationalProducts.module.css';
import TableCard from '@/components/Tables/TableCard/TableCard';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import {
  identicalArrays,
  useNationalProducts,
  useUpdateNationalProductsIslock,
} from '@/services/nationalproducts/nationalproducts.service';
import { NationalProductsDetails } from '@/types/nationalProducts.types';
import { PriceLockSwitchCell, MandatoryLockSwitchCell } from './internal/SwitchCell';
import Button from '@/components/Button/Button';
import { TextShorterCell } from './internal/TextShorterCell';
import { NationalProductsSelection } from '@/types/nationalProductsSelection.types';
import { useTranslation } from 'react-i18next';

const NationalProducts: FC = () => {
  const { t } = useTranslation();
  const { data: dataNationalProducts, isLoading, isError, refetch } = useNationalProducts(true);
  const [data, setData] = useState(dataNationalProducts?.data || []);
  const [originalData, setOriginalData] = useState(dataNationalProducts?.data || []);
  const [disableSaveChangesButton, setDisableSaveChangesButton] = useState(true);
  const { mutate: updateIsLockForNationalProduct } = useUpdateNationalProductsIslock();

  const columns = [
    {
      cell: MandatoryLockSwitchCell,
      header: t('nationalProducts.table.mandatory'),
      accessorKey: 'mandatoryProductLock',
      meta: { disableFilter: true },
    },
    {
      cell: PriceLockSwitchCell,
      header: t('nationalProducts.table.lockPrice'),
      accessorKey: 'priceLock',
      meta: { disableFilter: true },
    },
    {
      accessorFn: (originalRow: NationalProductsDetails) => originalRow.productName || '-',
      header: t('nationalProducts.table.product'),
      enableSorting: true,
    },
    {
      accessorFn: (originalRow: NationalProductsDetails) => originalRow.variantDescription || '-',
      cell: TextShorterCell,
      header: t('nationalProducts.table.variations'),
      enableSorting: true,
    },
  ];

  if (isError) {
    showToast({
      label: t('error.fetchingBundles'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingBundles',
    });
  }

  useEffect(() => {
    if (dataNationalProducts?.data) {
      setData(dataNationalProducts.data);
      setOriginalData(dataNationalProducts.data);
    }
  }, [dataNationalProducts]);

  const onButtonClick = () => {
    type NationalProductsIsLockUpdate = { variantId: string; isLock: boolean; mandatoryProductLock: boolean };
    const payload: NationalProductsIsLockUpdate[] = [];

    originalData.forEach((prod) => {
      const resultObject = data.filter((prodInternal) => prodInternal.variantId === prod.variantId)[0];

      if (
        resultObject.priceLock !== prod.priceLock ||
        resultObject.mandatoryProductLock !== prod.mandatoryProductLock
      ) {
        const nps: NationalProductsIsLockUpdate = {
          variantId: resultObject.variantId,
          isLock: resultObject.priceLock,
          mandatoryProductLock: resultObject.mandatoryProductLock,
        };
        payload.push(nps);
      }
    });

    updateIsLockForNationalProduct(payload as unknown as NationalProductsSelection[], {
      onSuccess: () => {
        showToast({
          id: 'editNationalPrice',
          label: t('nationalProducts.isLockUpdated'),
        });

        refetch();
      },
      onError: () => {
        showToast({
          id: 'errorUpdatingNationalProductsIsLock',
          label: t('error.updateNationalProduct'),
          toastType: ToastTypes.Error,
        });
      },
    });
  };

  useEffect(() => {
    const identicalData = identicalArrays(originalData, data);
    setDisableSaveChangesButton(identicalData);
  }, [data, originalData]);

  return (
    <>
      <Header title={t('nationalProducts.title')} breadcrumb={{ to: '/', label: t('breadcrumb.backToHome') }} />
      <div className={styles.page}>
        <div className={styles.tableLabel}>
          {t('nationalProducts.allProductsHeader')}
          <Button disabled={disableSaveChangesButton} onClick={() => onButtonClick()}>
            {t('nationalProducts.saveButton')}
          </Button>
        </div>
        <TableCard
          height="calc(75vh - 100px)"
          isLoading={isLoading}
          data={data}
          meta={{
            columns,
            data,
            // updateData is invoked by custom cells providing rowIndex, columnId, value as string, and isValid flag
            updateData: (rowIndex: number, columnId: string, value: string | boolean) => {
              setData((old) =>
                old.map((row, index) => {
                  if (index === rowIndex) {
                    // Convert string 'true'/'false' back to boolean for lock columns
                    const parsedValue =
                      columnId === 'priceLock' || columnId === 'mandatoryProductLock'
                        ? typeof value === 'string'
                          ? value === 'true'
                          : value
                        : value;
                    return {
                      ...old[rowIndex],
                      [columnId]: parsedValue,
                    } as NationalProductsDetails;
                  }
                  return row;
                }),
              );
            },
          }}
          columns={columns}
          enableFilters
        />
      </div>
    </>
  );
};

export default NationalProducts;
